import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandingProvider, useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LogOut, User, Menu, CreditCard, Sparkles, X } from "lucide-react";
import BillingBanner from "@/components/BillingBanner";
import SetupWizardGate from "@/components/SetupWizardGate";
import SetupReminderBanner from "@/components/SetupReminderBanner";
import EnvRibbon from "@/components/EnvRibbon";

const ROLE_LABEL = {
  super_admin: "Super Admin", company_admin: "Admin",
  team_admin: "Team Admin", employee: "Employee",
};

/**
 * Build the role-scoped nav links once and reuse them in the desktop bar AND
 * the mobile drawer so the two stay in lock-step.
 */
/**
 * Build a grouped navigation structure: the primary day-to-day links stay
 * top-level (Overview, Roster, Leave, Swaps, Reports), and admin/setup pages
 * collapse into "People", "Setup" and "Tools" dropdowns so the header does
 * not get crowded as the platform grows. Role-based visibility is preserved.
 *
 * Each entry is either:
 *   { to, label, testId }              -> direct top-level NavLink
 *   { label, testId, children: [...] } -> dropdown trigger with sub-items
 *
 * `buildLinks` returns the FLAT list used by the mobile drawer (so every
 * page is reachable without dropdowns on touch screens).
 */
function buildNavTree(role, featureFlags = {}) {
  if (role === "super_admin") {
    return [
      { to: "/app", label: "Platform", testId: "nav-platform" },
      { to: "/app/admin/companies", label: "Companies (CRM)", testId: "nav-companies-crm" },
      { to: "/app/admin/users", label: "Users", testId: "nav-users" },
      { to: "/app/companies", label: "Legacy companies", testId: "nav-companies-legacy" },
    ];
  }
  const top = [];
  top.push({ to: "/app", label: "Overview", testId: "nav-overview" });
  top.push(role === "employee"
    ? { to: "/app/my-roster", label: "My roster", testId: "nav-my-roster" }
    : { to: "/app/roster", label: "Roster", testId: "nav-roster" });
  top.push({ to: "/app/leave", label: "Leave", testId: "nav-leave" });
  top.push({ to: "/app/swaps", label: "Swaps", testId: "nav-swaps" });

  if (role === "company_admin" || role === "team_admin") {
    top.push({
      label: "Reports", testId: "nav-reports",
      children: [
        { to: "/app/reports", label: "Utilisation", testId: "nav-utilisation" },
        // Phase 2Z.s — Performance Focus Lite. Target vs Actual
        // tracking per employee with rota-linked insight and a
        // safe deep-link into RMI for under-target activities.
        { to: "/app/performance", label: "Performance Focus", testId: "nav-performance" },
        // V1: KPI page hidden from normal customer UI — still reachable
        // via direct URL for super-admin diagnostics.
        // { to: "/app/kpis",    label: "KPIs",         testId: "nav-kpis", advanced: true },
      ],
    });
    const people = [
      { to: "/app/employees", label: "Employees", testId: "nav-employees" },
      { to: "/app/skills", label: "Skills", testId: "nav-skills" },
    ];
    if (role === "company_admin") people.push({ to: "/app/teams", label: "Teams", testId: "nav-teams" });
    top.push({ label: "People", testId: "nav-people", children: people });
  }

  if (role === "company_admin") {
    // TP-Patterns.v1 Phase 2 (Feb 2026). When the company opts in
    // to the unified Working Patterns UI, replace the legacy
    // "Working patterns" (pattern_templates) + "Rotating shifts"
    // (shift_patterns) items with ONE clean "Working Patterns"
    // entry. The old routes stay reachable via direct URL for
    // fallback and diagnostics.
    const unifiedOn = !!featureFlags?.unified_patterns_ui_enabled;
    const setupChildren = [
      { to: "/app/categories",      label: "Categories",      testId: "nav-categories" },
      { to: "/app/skills",          label: "Skills",          testId: "nav-skills-setup" },
      { to: "/app/locations",       label: "Locations",       testId: "nav-locations" },
    ];
    if (unifiedOn) {
      setupChildren.push({
        to: "/app/working-patterns", label: "Working Patterns",
        testId: "nav-working-patterns",
      });
    } else {
      // TP-EmpUX (Feb 2026) -- "Rotating shifts" is no longer
      // surfaced in the customer-facing Setup dropdown. The
      // rotating shifts route stays reachable by direct URL for
      // existing tenants that opted in previously and for the
      // auto-materialise scheduler. Nothing on the backend has
      // been removed.
      setupChildren.push(
        { to: "/app/templates",       label: "Working patterns", testId: "nav-patterns" });
    }
    setupChildren.push(
      { to: "/app/bank-holidays",   label: "Holidays",        testId: "nav-holidays" });
    // Shift templates management page. Was reachable only by
    // direct URL for Workspace Admin; now surfaced in Setup so
    // managers can find it. Team Admin already has a top-level
    // entry (see below). Endpoint permissions are unchanged.
    setupChildren.push(
      { to: "/app/shift-templates", label: "Shift templates", testId: "nav-shift-templates" });
    top.push({
      label: "Setup", testId: "nav-setup",
      children: setupChildren,
    });
    top.push({
      label: "Tools", testId: "nav-tools",
      children: [
        // V1: Rota Style Automation is the only customer-facing
        // automation surface. The legacy `/app/automation` (gap-engine
        // settings, maturity levels, recommendation cards), In-context
        // tips, and Import are hidden from V1 customer UI but stay
        // reachable via direct URL for super-admin diagnostics.
        { to: "/app/rota-style", label: "Rota Style", testId: "nav-rota-style" },
        { to: "/app/rota-style/automation", label: "Automation V1",
          testId: "nav-rota-style-automation" },
        { to: "/app/activity", label: "Audit log", testId: "nav-activity" },
        { to: "/app/billing",  label: "Billing",  testId: "nav-billing" },
        { to: "/app/settings", label: "Settings", testId: "nav-settings" },
      ],
    });
  } else if (role === "team_admin") {
    // Team admins see Audit log & Shift templates at top level (no Setup group).
    top.push({ to: "/app/shift-templates", label: "Shift templates", testId: "nav-shift-templates" });
    // TP-1 -- Team admins see Rota Style read-only so they know what
    // pattern the Workspace Admin has set up for the company. Mutation
    // endpoints stay company_admin-only.
    top.push({ to: "/app/rota-style",       label: "Rota Style",      testId: "nav-rota-style" });
    top.push({ to: "/app/activity",        label: "Audit log",       testId: "nav-activity" });
  }
  return top;
}

function flattenNav(tree) {
  // For the mobile drawer: every page reachable without dropdowns.
  const out = [];
  for (const item of tree) {
    if (item.children) out.push(...item.children);
    else out.push(item);
  }
  // Phase 2Z.ai - dedupe by `to` so routes that legitimately live in
  // two desktop dropdowns (e.g. /app/skills under both "People" and
  // "Setup") only show once in the flat mobile drawer. Keeps the
  // first occurrence so the original ordering is preserved. Same
  // route - no behaviour change.
  const seen = new Set();
  return out.filter((l) => {
    if (!l.to || seen.has(l.to)) return false;
    seen.add(l.to);
    return true;
  });
}

export default function AppShell() {
  const { user } = useAuth();
  // Only fetch branding for tenant-bound roles. Super admins have no
  // company_id and should keep the NextRota mark.
  const enableBranding = !!user && user.role !== "super_admin";
  return (
    <BrandingProvider enabled={enableBranding}>
      <AppShellInner />
    </BrandingProvider>
  );
}

/**
 * Always renders the NextRota mark by default, swaps in the tenant logo only
 * once it has loaded successfully. If the tenant URL fails (deleted GridFS row,
 * network blip, CORS), `onError` falls back to the NextRota mark instead of
 * showing a broken image icon.
 */
function TenantOrDefaultLogo({ branding }) {
  const [tenantFailed, setTenantFailed] = useState(false);
  const tenantUrl = branding?.logo_url;
  const showTenant = !!tenantUrl && !tenantFailed;
  return showTenant ? (
    <img
      src={tenantUrl}
      alt=""
      data-testid="brand-logo-tenant"
      onError={() => setTenantFailed(true)}
      className="h-9 sm:h-10 w-auto max-w-[180px] object-contain"
    />
  ) : (
    <img
      src={(process.env.PUBLIC_URL || "") + "/nextrota-logo.png"}
      alt="NextRota"
      data-testid="brand-logo-default"
      className="h-9 sm:h-10 w-auto"
    />
  );
}

/**
 * Top-level nav link styled to match the previous flat header. Used for
 * direct routes (Overview, Roster, Leave, etc.) inside the grouped navTree.
 */
function NavTop({ item }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/app"}
      data-testid={item.testId}
      className={({ isActive }) =>
        `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive ? "bg-[#DBE7FE] text-[#1D4ED8]" : "text-[#1F2A44] hover:bg-slate-100"
        }`}>
      {item.label}
    </NavLink>
  );
}

/**
 * Dropdown trigger for grouped nav sections (People / Setup / Tools).
 * The trigger gets the active styling whenever any child route matches
 * so the user still sees where they are.
 */
function NavGroup({ item, pathname }) {
  const navigate = useNavigate();
  const activeChild = item.children.some((c) => pathname.startsWith(c.to));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={item.testId}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeChild ? "bg-[#DBE7FE] text-[#1D4ED8]" : "text-[#1F2A44] hover:bg-slate-100"
          }`}>
          {item.label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        {item.children.map((c) => (
          <DropdownMenuItem
            key={c.to}
            data-testid={c.testId}
            onClick={() => navigate(c.to)}
            className="flex items-center justify-between gap-2">
            <span>{c.label}</span>
            {c.advanced && (
              <span className="text-[9px] uppercase tracking-widest text-[#1D4ED8] px-1 py-0 rounded bg-[#DBE7FE] border border-[#BFD3FB]">
                Advanced
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AppShellInner() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role;
  // TP-Patterns.v1 Phase 2 (Feb 2026): fetch company feature flags
  // so the nav can gate `Setup -> Working Patterns` behind the
  // `unified_patterns_ui_enabled` flag. Falls back to legacy nav
  // when the flag is off or the fetch fails.
  const [featureFlags, setFeatureFlags] = useState({});
  useEffect(() => {
    if (!user || role === "super_admin") return;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const { data } = await api.get("/billing/me");
        setFeatureFlags(data?.feature_flags || {});
      } catch { /* keep legacy nav */ }
    })();
  }, [user, role]);
  const navTree = buildNavTree(role, featureFlags);
  const flatLinks = flattenNav(navTree);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Auto-close the drawer on route change so the user lands on the new page
  // without an extra tap. Effect-only - no other state interaction needed.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const initials = (user?.name || user?.email || "U")
    .split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase();

  // Decode the non-httpOnly `impersonation` cookie (set by the backend on
  // /admin/impersonate/{id}). Format: `<impersonator_id>::<target_id>::<name>`.
  // Pure derivation - no side-effects, recomputed each render.
  const impersonationCookie = typeof document !== "undefined"
    ? (document.cookie.split("; ").find(r => r.startsWith("impersonation=")) || "").split("=")[1]
    : "";
  const impersonatedName = impersonationCookie ? decodeURIComponent(impersonationCookie.split("::")[2] || "") : null;

  const exitImpersonation = async () => {
    try {
      const { api } = await import("@/lib/api");
      await api.post("/admin/impersonate/exit");
    } finally {
      // Clear the flag cookie client-side (httpOnly access_token is also cleared
      // by the server). Hard reload so AuthContext rehydrates as super_admin
      // via the refresh-token cookie.
      document.cookie = "impersonation=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      window.location.href = "/app";
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F9FE]">
      <EnvRibbon />
      {impersonatedName && (
        <div className="bg-red-600 text-white text-sm px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-50"
             data-testid="impersonation-banner">
          <div className="truncate">
            <span className="font-semibold">Impersonating {impersonatedName}.</span>
            <span className="opacity-90 ml-2 hidden sm:inline">Destructive actions are disabled. All activity is audit-logged.</span>
          </div>
          <button onClick={exitImpersonation} data-testid="exit-impersonation-btn"
            className="px-3 py-1 rounded-md bg-white text-red-700 font-semibold text-xs hover:bg-red-50">
            Exit
          </button>
        </div>
      )}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-10">
            {/* Mobile hamburger - md:hidden to stay invisible on desktop */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open navigation"
              className="md:hidden -ml-1.5 p-2 rounded-md text-[#1F2A44] hover:bg-slate-100 active:bg-slate-200">
              <Menu className="h-5 w-5"/>
            </button>
            <NavLink to="/app" className="flex items-center" data-testid="brand-link">
              <TenantOrDefaultLogo branding={branding}/>
            </NavLink>
            <nav className="hidden md:flex items-center gap-1">
              {navTree.map((item) => item.children
                ? <NavGroup key={item.label} item={item} pathname={location.pathname}/>
                : <NavTop key={item.to} item={item}/>
              )}
            </nav>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn"
                className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors">
                <div className="h-8 w-8 rounded-full bg-slate-900 text-white grid place-items-center text-xs font-medium">{initials}</div>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium leading-tight">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5773]">{ROLE_LABEL[role]}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="account-link" onClick={()=>navigate("/app/account")}>
                <User className="h-4 w-4 mr-2"/>My account
              </DropdownMenuItem>
              {role === "company_admin" && (
                <DropdownMenuItem data-testid="user-menu-billing" onClick={()=>navigate("/app/billing")}>
                  <CreditCard className="h-4 w-4 mr-2"/>Manage billing
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="logout-btn" onClick={async ()=>{ await logout(); navigate("/login"); }}>
                <LogOut className="h-4 w-4 mr-2"/>Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <BillingBanner />

      {/* Mobile navigation drawer - same role-scoped link set as desktop. */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col h-[100dvh]" data-testid="mobile-nav-drawer">
          <SheetHeader className="px-5 py-4 border-b border-slate-100 text-left shrink-0">
            <SheetTitle className="font-display text-lg flex items-center gap-2">
              <img src={process.env.PUBLIC_URL + "/nextrota-logo.png"} alt="NextRota" className="h-9 w-auto"/>
            </SheetTitle>
            <div className="text-xs text-[#4B5773] truncate">
              {user?.name} · <span className="uppercase tracking-widest">{ROLE_LABEL[role]}</span>
            </div>
          </SheetHeader>
          <nav className="p-2 flex flex-col flex-1 overflow-y-auto" data-testid="mobile-nav-scroll">
            {flatLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/app"}
                data-testid={`mobile-${l.testId || `nav-${l.label.toLowerCase().replace(/\s+/g,"-")}`}`}
                onClick={()=>setDrawerOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-[15px] font-medium transition-colors flex items-center justify-between gap-2 ${
                    isActive ? "bg-[#DBE7FE] text-[#1D4ED8]" : "text-[#1F2A44] hover:bg-slate-100"
                  }`}>
                <span>{l.label}</span>
                {l.advanced && (
                  <span className="text-[9px] uppercase tracking-widest text-[#1D4ED8] px-1 py-0 rounded bg-[#DBE7FE] border border-[#BFD3FB]">
                    Advanced
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-2 border-t border-slate-100 shrink-0">
            <button data-testid="mobile-account-link"
              onClick={()=>{ setDrawerOpen(false); navigate("/app/account"); }}
              className="flex items-center gap-2 w-full px-4 py-3 rounded-lg text-sm text-[#1F2A44] hover:bg-slate-100">
              <User className="h-4 w-4"/>My account
            </button>
            <button data-testid="mobile-logout-btn"
              onClick={async ()=>{ setDrawerOpen(false); await logout(); navigate("/login"); }}
              className="flex items-center gap-2 w-full px-4 py-3 rounded-lg text-sm text-red-600 hover:bg-red-50">
              <LogOut className="h-4 w-4"/>Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <SetupReturnBanner />
        <SetupReminderBanner />
        <Outlet />
      </main>
      <SetupWizardGate />
    </div>
  );
}


// Phase 2Z.k polish — when the customer leaves the Guided Setup
// Assistant to a link-out destination (Employees, Working Patterns,
// Rota Style, Drafts, Automation V1), the assistant sets a session
// flag. This banner mounts on every protected page and offers a
// one-click return. Hidden whenever the user is already on /app/setup
// or has dismissed the session.
function SetupReturnBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  useEffect(() => {
    setShow(sessionStorage.getItem("nextrota_setup_return") === "1"
            && !location.pathname.startsWith("/app/setup"));
  }, [location.pathname]);
  if (!show) return null;
  return (
    <div className="mb-4 rounded-xl border border-[#CBE0FB] bg-gradient-to-br from-[#EFF3FB] to-[#E4EEFE] px-4 py-3 flex items-center gap-3"
         data-testid="setup-return-banner">
      <div className="w-8 h-8 rounded-full bg-white shadow-sm border border-[#CBE0FB] flex items-center justify-center shrink-0">
        <Sparkles className="h-4 w-4 text-[#2563EB]" />
      </div>
      <div className="flex-1">
        <p className="text-[12px] font-semibold text-[#0B1A36]">Continue setup</p>
        <p className="text-[11px] text-[#4B5773]">
          You&apos;re completing your account setup. Return to Guided Setup when you&apos;re finished here.
        </p>
      </div>
      <button type="button"
              onClick={() => navigate("/app/setup")}
              className="text-[12px] font-medium text-white bg-[#2563EB] hover:bg-[#1D4FD6] px-3 py-1.5 rounded-md"
              data-testid="setup-return-back-btn">
        Back to setup
      </button>
      <button type="button"
              onClick={() => { sessionStorage.removeItem("nextrota_setup_return"); setShow(false); }}
              className="text-[#4B5773] hover:text-[#0B1A36]"
              data-testid="setup-return-dismiss-btn"
              aria-label="Dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
